/**
 * Unified Template Contract (Issue #1533)
 *
 * Replaces three legacy template namespaces:
 *   - DocumentTemplate          (PDFs synced from DocuSeal)
 *   - UserTemplate(TERMSHEET)   (structured JSON termsheets, wizard-built)
 *   - getAllTermsheetTemplates  (in-process system seed array)
 *
 * Phase 1 scope: this contract is the API surface for the new `templates.*`
 * tRPC router and the `TemplateService` gRPC service. The legacy
 * `documentTemplates.*` and `userTemplates(TERMSHEET).*` and
 * `termsheets.systemTemplates.*` procedures become thin proxies that route
 * through these types.
 *
 * Multi-tenancy: every per-tenant query carries `businessId` per CLAUDE.md §2.
 * Platform defaults live in `TemplateSeed` (no `businessId` column at all);
 * tenants fork into `Template` rows via `parentSeedId` per the #1522
 * platform-defaults pattern.
 *
 * Versioning (Phase 2): `Template.version` + `parentTemplateId` self-FK.
 * Phase 1 ships at version=1 for every row.
 *
 * BREAKING CHANGE LOG:
 * - None yet. Initial version.
 */

import { z } from 'zod';

// ============================================================================
// ENUMS — mirror the Prisma enums so the API boundary has runtime validation
// ============================================================================

export const TemplateKind = {
  CONTRACT: 'CONTRACT',
  TERMSHEET: 'TERMSHEET',
  DISCLOSURE: 'DISCLOSURE',
  GENERIC: 'GENERIC',
  ADHOC: 'ADHOC',
} as const;
export type TemplateKindValue = (typeof TemplateKind)[keyof typeof TemplateKind];
export const TemplateKindSchema = z.enum(['CONTRACT', 'TERMSHEET', 'DISCLOSURE', 'GENERIC', 'ADHOC']);

export const TemplateContentType = {
  PDF: 'PDF',
  STRUCTURED: 'STRUCTURED',
} as const;
export type TemplateContentTypeValue = (typeof TemplateContentType)[keyof typeof TemplateContentType];
export const TemplateContentTypeSchema = z.enum(['PDF', 'STRUCTURED']);

export const TemplateSource = {
  SYSTEM: 'SYSTEM',
  AUTHORED: 'AUTHORED',
  ADHOC: 'ADHOC',
} as const;
export type TemplateSourceValue = (typeof TemplateSource)[keyof typeof TemplateSource];
export const TemplateSourceSchema = z.enum(['SYSTEM', 'AUTHORED', 'ADHOC']);

export const TemplateStatus = {
  DRAFT: 'DRAFT',
  ACTIVE: 'ACTIVE',
  ARCHIVED: 'ARCHIVED',
} as const;
export type TemplateStatusValue = (typeof TemplateStatus)[keyof typeof TemplateStatus];
export const TemplateStatusSchema = z.enum(['DRAFT', 'ACTIVE', 'ARCHIVED']);

/**
 * Saga-internal provisioning state for tenant-owned PDF uploads (Phase 7 — #1531).
 *
 * `TemplateProvisioningStatus` is ORTHOGONAL to `TemplateStatus`:
 *   - `TemplateStatus` (DRAFT|ACTIVE|ARCHIVED) is the publish lifecycle a tenant
 *     admin sees (Save Draft / Publish / Archive).
 *   - `TemplateProvisioningStatus` is the saga state for the GCS+DocuSeal upload
 *     pipeline. `PENDING_DOCUSEAL_PROVISIONING` and `PROVISIONING_FAILED` are
 *     half-formed states managed via the `/configure` retry UX — they are NEVER
 *     valid filter values on the picker API. The picker schema explicitly
 *     restricts `status` to `TemplateStatusSchema` to keep these from leaking
 *     in as user-controllable filters.
 *
 * Plan: docs/plans/20260429-PLAN-document-templates-unified.md §6.4.
 */
export const TemplateProvisioningStatus = {
  NONE: 'NONE',
  PENDING_DOCUSEAL_PROVISIONING: 'PENDING_DOCUSEAL_PROVISIONING',
  PROVISIONED: 'PROVISIONED',
  PROVISIONING_FAILED: 'PROVISIONING_FAILED',
} as const;
export type TemplateProvisioningStatusValue =
  (typeof TemplateProvisioningStatus)[keyof typeof TemplateProvisioningStatus];
export const TemplateProvisioningStatusSchema = z.enum([
  'NONE',
  'PENDING_DOCUSEAL_PROVISIONING',
  'PROVISIONED',
  'PROVISIONING_FAILED',
]);

// Issue #1545 retired the ContractType Prisma enum in favour of a per-tenant
// `ContractSubtype` table seeded from `PLATFORM_DEFAULT_CONTRACT_SUBTYPES`.
// Legacy callers that still want the old value union (uppercase enum values
// like REFERRAL_BROKER) can construct it from the platform defaults at
// runtime — no schema validator is offered because new callers should use
// `SubtypeSlugSchema` (lowercase slugs).
//
// `ContractTypeValue` is preserved as a string-literal union of the legacy
// uppercase shape so #1531 Phase 6/Phase 10 callers continue to typecheck.
// The mapping mirrors the slugs in `PLATFORM_DEFAULT_CONTRACT_SUBTYPES`
// uppercased — see `get-data-points-for-kind.ts` for the consumers.
export type ContractTypeValue =
  | 'REFERRAL_BROKER'
  | 'CORRESPONDENT_LENDING'
  | 'WHOLESALE'
  | 'CHANNEL_PARTNER'
  | 'NDA'
  | 'DPA'
  | 'MSA'
  | 'SLA';

// Contract subtype slugs are validated as opaque short strings. The full set
// of valid slugs is enforced server-side against the merged-view catalog
// (`ContractSubtypeService.listForBusiness`) so tenants can adopt new
// subtypes without a TS-side enum bump. `1` to `64` characters covers the
// platform defaults plus generous room for tenant-original additions.
const SUBTYPE_SLUG_LENGTH_MIN = 1;
const SUBTYPE_SLUG_LENGTH_MAX = 64;
export const SubtypeSlugSchema = z.string().min(SUBTYPE_SLUG_LENGTH_MIN).max(SUBTYPE_SLUG_LENGTH_MAX);
export type SubtypeSlugValue = z.infer<typeof SubtypeSlugSchema>;

// ============================================================================
// CORE DTO — Template
// ============================================================================

/**
 * Phase 1 backfill preserves UUIDs from `DocumentTemplate.id` and
 * `UserTemplate.id`. New rows generated post-migration get fresh UUIDs.
 * `SigningEnvelope.templateId` continues to resolve via the same UUID — no
 * `legacyDocumentTemplateId` column needed.
 */
export const TemplateSchema = z.object({
  id: z.string().uuid(),
  businessId: z.string().uuid(),
  kind: TemplateKindSchema,
  contentType: TemplateContentTypeSchema,
  name: z.string().min(1),
  description: z.string().nullable(),
  category: z.string().nullable(),
  subcategory: z.string().nullable(),
  tags: z.array(z.string()).default([]),
  source: TemplateSourceSchema,
  status: TemplateStatusSchema,

  // PDF-backed fields (when contentType === 'PDF')
  externalId: z.string().nullable(), // DocuSeal template ID (string form, matches legacy DocumentTemplate.externalId)
  fields: z.unknown().nullable(),
  submitters: z.unknown().nullable(),
  previewUrl: z.string().nullable(),
  fieldCount: z.number().int().nonnegative(),

  // Structured fields (when contentType === 'STRUCTURED')
  data: z.unknown().nullable(),

  // Versioning chain
  version: z.number().int().positive(),
  parentTemplateId: z.string().uuid().nullable(),

  // Platform-default fork link (per #1522). Same string-not-uuid relaxation
  // as `TemplateSeed.id` — legacy seeds use stable IDs like `tpl-term-*`.
  parentSeedId: z.string().min(1).max(200).nullable(),

  // Subtype linkage (Phase 3 — Issue #1533, renamed from contractType in #1545)
  subtypeSlug: SubtypeSlugSchema.nullable(),
  isDefaultForType: z.boolean(),

  // Adhoc (one-shot per case)
  caseId: z.string().nullable(),
  createdByName: z.string().nullable(),

  // Saga-internal provisioning state (Phase 7 — orthogonal to status). Defaults
  // to `NONE` for legacy / DocuSeal-only rows where no GCS upload saga ran.
  provisioningStatus: TemplateProvisioningStatusSchema.default('NONE'),

  usageCount: z.number().int().nonnegative(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TemplateDto = z.infer<typeof TemplateSchema>;

// ============================================================================
// CORE DTO — TemplateSeed (platform-managed; no businessId)
// ============================================================================

// Note: `id` is `z.string().min(1)` (not uuid) because the canonical platform
// seeds use stable human-readable IDs like `tpl-term-bridge-loan` (per
// `1-shared-types/src/templates/termsheet-templates.ts` and `TERMSHEET_TEMPLATE_IDS`).
// Future seeds created via `TemplateSeed.create` get UUIDs; the legacy IDs
// remain valid for the in-process seed array.
export const TemplateSeedSchema = z.object({
  id: z.string().min(1).max(200),
  kind: TemplateKindSchema,
  contentType: TemplateContentTypeSchema,
  name: z.string().min(1),
  description: z.string().nullable(),
  category: z.string().nullable(),
  subcategory: z.string().nullable(),
  tags: z.array(z.string()).default([]),
  data: z.unknown().nullable(),
  fields: z.unknown().nullable(),
  externalId: z.string().nullable(),
  isActive: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime(),
});
export type TemplateSeedDto = z.infer<typeof TemplateSeedSchema>;

// ============================================================================
// REQUEST / RESPONSE — list, get
// ============================================================================

/**
 * Picker-safe list input.
 *
 * `status` is explicitly typed `TemplateStatusSchema` (DRAFT|ACTIVE|ARCHIVED) and
 * NOT a union with `TemplateProvisioningStatusSchema` — the saga-internal states
 * (`PENDING_DOCUSEAL_PROVISIONING`, `PROVISIONING_FAILED`) are managed via the
 * `/configure` retry UX, not the picker API. Letting them leak in as filter
 * values would let callers query half-formed forks and surface them in
 * partner-invite wizards. See §6.4 of the v4 plan.
 *
 * Pagination: `limit` defaults to 50 with a hard ceiling of 100 (picker views
 * are interactive lists; large pages don't make sense). `cursor` (UUID) is the
 * forward-only pagination token; `page` remains for back-compat with callers
 * that haven't migrated yet.
 */
export const ListTemplatesRequestSchema = z.object({
  businessId: z.string().uuid(),
  kind: TemplateKindSchema.optional(),
  contentType: TemplateContentTypeSchema.optional(),
  subtypeSlug: SubtypeSlugSchema.optional(),
  status: TemplateStatusSchema.optional(),
  source: TemplateSourceSchema.optional(),
  search: z.string().optional(),
  page: z.number().int().positive().optional(),
  limit: z.number().int().positive().max(100).default(50),
  cursor: z.string().uuid().optional(),
});
export type ListTemplatesRequest = z.infer<typeof ListTemplatesRequestSchema>;

/**
 * Picker row — discriminated union surface for the merged-view list.
 *
 * Phase 7 §6.4 specifies that the picker API returns BOTH per-tenant
 * `Template` rows AND un-forked `TemplateSeed` rows in a single envelope so the
 * frontend can render "Use platform default" and "Tenant fork" entries
 * together. `kind: 'template' | 'seed'` is the explicit discriminator.
 *
 * - `kind === 'template'`: `id` is a Template UUID, `provisioningStatus` may be
 *   any of the 4 saga states, `mustForkBeforeUse` is always `false`.
 * - `kind === 'seed'`: `id` is the seed's stable string ID (e.g.
 *   `tpl-term-bridge-loan`), `provisioningStatus` is always `'NONE'`,
 *   `mustForkBeforeUse` reflects the seed's legal-banner gate, and `fork` is
 *   present iff the calling tenant already has a fork of this seed.
 *
 * The actual merged-view query implementation lands in a follow-up Phase 7
 * commit; this schema is published now so downstream callers (frontend,
 * agent-service skill, the partner-invite wizard) can adopt the discriminator
 * shape today.
 */
export const PickerRowKindSchema = z.enum(['template', 'seed']);
export type PickerRowKind = z.infer<typeof PickerRowKindSchema>;

export const PickerRowSchema = z.object({
  id: z.string().min(1).max(200), // Template UUID OR TemplateSeed stable ID
  kind: PickerRowKindSchema,
  templateKind: TemplateKindSchema,
  contentType: TemplateContentTypeSchema,
  // Issue #1545 retired the ContractType Prisma enum; the wire field is now
  // a free-form slug (validated against ContractSubtype.slug server-side via
  // SubtypeSlugSchema). `.nullable()` retained: ADHOC/TERMSHEET/DISCLOSURE
  // rows have no contractType.
  contractType: SubtypeSlugSchema.nullable(),
  name: z.string().min(1),
  description: z.string().nullable(),
  status: TemplateStatusSchema,
  provisioningStatus: TemplateProvisioningStatusSchema,
  mustForkBeforeUse: z.boolean(),
  // Present only when kind === 'seed' AND the calling tenant already has a fork
  // of this seed. Lets the picker UI offer "Open existing fork" vs "Fork now".
  fork: z
    .object({
      id: z.string().uuid(),
      updatedAt: z.string().datetime(),
    })
    .optional(),
});
export type PickerRow = z.infer<typeof PickerRowSchema>;

export const ListTemplatesResponseSchema = z.object({
  data: z.array(TemplateSchema),
  total: z.number().int().nonnegative(),
  // Cursor-based pagination — null when no further pages exist. Existing
  // callers that paginate via `page` can ignore this field.
  nextCursor: z.string().uuid().nullable().optional(),
});
export type ListTemplatesResponse = z.infer<typeof ListTemplatesResponseSchema>;

/**
 * Picker-shaped response for the merged-view query. Returns the discriminated
 * `PickerRow` union (Templates + un-forked Seeds) instead of raw `Template`
 * rows so the frontend gets full kind/provisioning/mustFork context per row.
 *
 * Phase 7 follow-up commit will wire this to the gRPC merged-view query; for
 * now the schema is published so consumers can adopt the shape.
 */
export const ListTemplatesPickerResponseSchema = z.object({
  items: z.array(PickerRowSchema),
  nextCursor: z.string().uuid().nullable(),
});
export type ListTemplatesPickerResponse = z.infer<typeof ListTemplatesPickerResponseSchema>;

export const GetTemplateRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
});
export type GetTemplateRequest = z.infer<typeof GetTemplateRequestSchema>;

// ============================================================================
// REQUEST / RESPONSE — create, update, archive
// ============================================================================

/**
 * Create a new Template. Phase 1: every new Template starts at version=1
 * with status=DRAFT. The PublishVersion mutation (Phase 2) flips status to
 * ACTIVE.
 *
 * Note: `kind` + `contentType` are required. PDF-backed templates created
 * through this path provide `externalId` after a DocuSeal upload; structured
 * templates provide `data`. The service-layer validator rejects rows that
 * have neither (or both) for the wrong content type.
 */
export const CreateTemplateRequestSchema = z.object({
  businessId: z.string().uuid(),
  kind: TemplateKindSchema,
  contentType: TemplateContentTypeSchema,
  name: z.string().min(1).max(200),
  description: z.string().max(2000).optional(),
  category: z.string().max(100).optional(),
  subcategory: z.string().max(100).optional(),
  tags: z.array(z.string()).default([]),
  source: TemplateSourceSchema.default('AUTHORED'),
  externalId: z.string().optional(), // PDF only
  fields: z.unknown().optional(),
  submitters: z.unknown().optional(),
  previewUrl: z.string().url().optional(),
  fieldCount: z.number().int().nonnegative().default(0),
  data: z.unknown().optional(), // STRUCTURED only
  subtypeSlug: SubtypeSlugSchema.optional(),
  isDefaultForType: z.boolean().default(false),
  caseId: z.string().optional(),
  parentSeedId: z.string().min(1).max(200).optional(),
});
export type CreateTemplateRequest = z.infer<typeof CreateTemplateRequestSchema>;

export const UpdateTemplateRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
  name: z.string().min(1).max(200).optional(),
  description: z.string().max(2000).nullable().optional(),
  category: z.string().max(100).nullable().optional(),
  subcategory: z.string().max(100).nullable().optional(),
  tags: z.array(z.string()).optional(),
  status: TemplateStatusSchema.optional(),
  fields: z.unknown().optional(),
  submitters: z.unknown().optional(),
  previewUrl: z.string().url().nullable().optional(),
  fieldCount: z.number().int().nonnegative().optional(),
  data: z.unknown().optional(),
  subtypeSlug: SubtypeSlugSchema.nullable().optional(),
  isDefaultForType: z.boolean().optional(),
});
export type UpdateTemplateRequest = z.infer<typeof UpdateTemplateRequestSchema>;

export const ArchiveTemplateRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
});
export type ArchiveTemplateRequest = z.infer<typeof ArchiveTemplateRequestSchema>;

export const DuplicateTemplateRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
  name: z.string().min(1).max(200), // new name for the duplicate
});
export type DuplicateTemplateRequest = z.infer<typeof DuplicateTemplateRequestSchema>;

// ============================================================================
// REQUEST / RESPONSE — uploadPdf (DocuSeal upload + cache)
// ============================================================================

// 25 MB matches the limit used by the existing `documentTemplates.create`
// flow (the practical DocuSeal upload ceiling). Larger files would need a
// presigned-URL flow, which is out of Phase 4 scope.
const MAX_PDF_UPLOAD_BYTES = 25 * 1024 * 1024;

export const UploadPdfTemplateRequestSchema = z.object({
  businessId: z.string().uuid(),
  // `kind` and `contentType` are independent dimensions. This RPC always
  // produces contentType=PDF (the upload IS a PDF); kind says what the PDF
  // represents. All four kinds are valid here — a TERMSHEET can be PDF
  // (uploaded slip) or STRUCTURED (wizard-built); the structured path uses
  // CreateTemplate, not this RPC.
  kind: TemplateKindSchema,
  name: z.string().min(1).max(200),
  category: z.string().max(100).optional(),
  subcategory: z.string().max(100).optional(),
  tags: z.array(z.string()).default([]),
  subtypeSlug: SubtypeSlugSchema.optional(),
  // PDF file upload — base64-encoded contents + filename.
  // The api-gateway uploadPdf tRPC procedure POSTs these to the integration
  // service's /connectors/docuseal/templates endpoint, which provisions a
  // DocuSeal template and returns the externalId + fields + preview URL.
  // We then create the Template row with that metadata. Callers who already
  // have a DocuSeal externalId should use `templates.create` directly.
  fileBase64: z
    .string()
    .min(1)
    .refine((v) => Buffer.byteLength(v, 'base64') <= MAX_PDF_UPLOAD_BYTES, 'File exceeds the 25 MB upload limit'),
  fileName: z.string().trim().min(1).max(255),
});
export type UploadPdfTemplateRequest = z.infer<typeof UploadPdfTemplateRequestSchema>;

// ============================================================================
// REQUEST / RESPONSE — replacePdf (Phase 7 commit 5 — #1531 v4 §6.2)
// ============================================================================

/**
 * SHA-256 hex digest — exactly 64 lowercase hex chars. This is the wire
 * shape both the browser (Web Crypto API `crypto.subtle.digest('SHA-256', …)`)
 * and the gateway emit; the CRM service recomputes server-side and rejects
 * mismatches with INVALID_ARGUMENT.
 *
 * We accept lowercase only because that's what `Array.from(...).map(b =>
 * b.toString(16).padStart(2, '0')).join('')` yields and what
 * `node:crypto.createHash('sha256').digest('hex')` yields. Mixing case
 * silently masks paste-related typos, so we reject it.
 */
const Sha256HexSchema = z
  .string()
  .regex(/^[a-f0-9]{64}$/, 'expectedSha256 must be 64 lowercase hex chars (SHA-256 digest)');

/**
 * Replace the underlying PDF on a configured template. SHA-256 layout-
 * unchanged detection: identical bytes → no-op; different bytes → status=DRAFT,
 * provisioningStatus=PENDING_DOCUSEAL_PROVISIONING, every TemplateFieldMapping
 * flips to isStale=true.
 *
 * `expectedSha256` is the client's pre-upload hash (computed via Web Crypto
 * API in the browser). The server recomputes from the bytes and rejects
 * mismatches — cheap defense against MITM/proxy corruption between the
 * browser and the gateway.
 */
export const ReplacePdfTemplateRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
  fileBase64: z
    .string()
    .min(1)
    .refine((v) => Buffer.byteLength(v, 'base64') <= MAX_PDF_UPLOAD_BYTES, 'File exceeds the 25 MB upload limit'),
  fileName: z.string().trim().min(1).max(255),
  expectedSha256: Sha256HexSchema.optional(),
});
export type ReplacePdfTemplateRequest = z.infer<typeof ReplacePdfTemplateRequestSchema>;

/**
 * Outcome of a Replace-PDF operation. Matches the gRPC response `outcome`
 * field. Layout-similar (page count + dimensions) is reserved for a follow-
 * up commit — until then layout-similar collapses into 'updated'.
 */
export const ReplacePdfOutcomeSchema = z.enum(['identical', 'updated']);
export type ReplacePdfOutcome = z.infer<typeof ReplacePdfOutcomeSchema>;

export const ReplacePdfTemplateResponseSchema = z.object({
  template: TemplateSchema,
  outcome: ReplacePdfOutcomeSchema,
  // The hash the row had BEFORE this replace (null when the template was
  // created from an upload that pre-dated the pdfSha256 column).
  oldSha256: Sha256HexSchema.nullable(),
  // The hash the row has AFTER this replace.
  newSha256: Sha256HexSchema,
});
export type ReplacePdfTemplateResponse = z.infer<typeof ReplacePdfTemplateResponseSchema>;

// ============================================================================
// REQUEST / RESPONSE — seeds + fork
// ============================================================================

export const ListTemplateSeedsRequestSchema = z.object({
  kind: TemplateKindSchema.optional(),
  contentType: TemplateContentTypeSchema.optional(),
});
export type ListTemplateSeedsRequest = z.infer<typeof ListTemplateSeedsRequestSchema>;

export const ListTemplateSeedsResponseSchema = z.object({
  data: z.array(TemplateSeedSchema),
  total: z.number().int().nonnegative(),
});
export type ListTemplateSeedsResponse = z.infer<typeof ListTemplateSeedsResponseSchema>;

/**
 * Fork a TemplateSeed into a per-tenant Template. The new Template starts at
 * version=1, status=DRAFT, source=SYSTEM, with parentSeedId pointing back to
 * the seed. Tenants can then edit freely without affecting the seed.
 *
 * Note: `seedId` is a string (not a UUID) because the canonical platform
 * seeds use stable human-readable IDs like `tpl-term-bridge-loan` (per
 * `1-shared-types/src/templates/termsheet-templates.ts`). Future seeds
 * created via `TemplateSeed.create` will get UUIDs, but the legacy IDs
 * stay supported.
 */
export const ForkSeedRequestSchema = z.object({
  businessId: z.string().uuid(),
  seedId: z.string().min(1).max(200),
  // Optional override; defaults to the seed's name.
  name: z.string().min(1).max(200).optional(),
});
export type ForkSeedRequest = z.infer<typeof ForkSeedRequestSchema>;

// ============================================================================
// SENSITIVE-KEY ADMIN OVERRIDE (Issue #1531 Phase 7 commit 6)
// ============================================================================
// Per-template opt-out of platform sensitive-canonical-key blocks. Reason text
// is REQUIRED at ≥20 chars (audit-log retention requirement) and surfaces in
// the post-update audit metadata. CRM service additionally validates the
// canonical_key against `SENSITIVE_CANONICAL_KEYS_BY_KIND[kind]` for the
// target Template's kind, and rejects ADHOC kind entirely (overrides don't
// apply to one-shot templates).
export const AddSensitiveOverrideRequestSchema = z.object({
  businessId: z.string().uuid(),
  templateId: z.string().uuid(),
  canonicalKey: z.string().min(1).max(200),
  reason: z.string().min(20, 'Reason must be at least 20 characters for audit retention').max(500),
});
export type AddSensitiveOverrideRequest = z.infer<typeof AddSensitiveOverrideRequestSchema>;

export const RemoveSensitiveOverrideRequestSchema = z.object({
  businessId: z.string().uuid(),
  templateId: z.string().uuid(),
  canonicalKey: z.string().min(1).max(200),
  // Reason is required (≥20 chars) for the same 7-year audit-retention
  // requirement as the add path — every sensitive-key mutation must carry
  // a rationale so compliance audits can reconstruct the full lifecycle.
  reason: z.string().min(20, 'Reason must be at least 20 characters for audit retention').max(500),
});
export type RemoveSensitiveOverrideRequest = z.infer<typeof RemoveSensitiveOverrideRequestSchema>;

// ============================================================================
// REQUEST / RESPONSE — versioning (Phase 2 stubs; Phase 1 returns NOT_IMPLEMENTED)
// ============================================================================

// Names are Template-prefixed to avoid collision with featurestore.contract /
// prompt.contract, which both export plain `ListVersionsRequest` / `Response`.
export const PublishTemplateVersionRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
  changelog: z.string().max(2000).optional(),
});
export type PublishTemplateVersionRequest = z.infer<typeof PublishTemplateVersionRequestSchema>;

export const ListTemplateVersionsRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
});
export type ListTemplateVersionsRequest = z.infer<typeof ListTemplateVersionsRequestSchema>;

export const ListTemplateVersionsResponseSchema = z.object({
  data: z.array(TemplateSchema),
  total: z.number().int().nonnegative(),
});
export type ListTemplateVersionsResponse = z.infer<typeof ListTemplateVersionsResponseSchema>;

export const RevertTemplateToVersionRequestSchema = z.object({
  businessId: z.string().uuid(),
  id: z.string().uuid(),
  version: z.number().int().positive(),
});
export type RevertTemplateToVersionRequest = z.infer<typeof RevertTemplateToVersionRequestSchema>;

// ============================================================================
// REQUEST / RESPONSE — listForContractType (Phase 3 — partner-invite wizard)
// ============================================================================

/**
 * What the partner-invite wizard step 3 reads from to populate the per-card
 * template+version dropdown. Always returns ACTIVE templates only; archived
 * versions are surfaced separately via `listVersions(id)`. The procedure
 * name (`listForContractType`) is unchanged for tRPC URL stability; only
 * the input field renamed `contractType` → `subtypeSlug` (#1545).
 *
 * Pagination matches `ListTemplatesRequestSchema`: `limit` defaults to 50, max
 * 100; `cursor` is the forward-only UUID pagination token.
 */
export const ListTemplatesForContractTypeRequestSchema = z.object({
  businessId: z.string().uuid(),
  subtypeSlug: SubtypeSlugSchema,
  limit: z.number().int().positive().max(100).default(50),
  cursor: z.string().uuid().optional(),
});
export type ListTemplatesForContractTypeRequest = z.infer<typeof ListTemplatesForContractTypeRequestSchema>;

// ============================================================================
// gRPC WIRE SHAPES — `TemplateRecord` / `TemplateSeedRecord`
// ============================================================================
//
// Mirror `proto/crm-service.proto`'s `TemplateRecord` / `TemplateSeedRecord`
// messages (snake_case, optional fields, JSON-encoded sub-payloads). Centralized
// here so api-gateway and integration-service share one source of truth —
// proto changes update one type, both consumers follow.

/** Wire-format `TemplateRecord` from `crm-service.proto` (snake_case). */
export interface TemplateRecordWire {
  id: string;
  business_id: string;
  kind: string;
  status: string;
  // PDF-backed fields (when content_type === 'PDF')
  external_id?: string;
  fields_json?: string;
  submitters_json?: string;
  preview_url?: string;
  field_count?: number;
  // Structured fields (when content_type === 'STRUCTURED')
  data_json?: string;
  // Phase 7 saga columns (#1531 v4 §6.2 / §6.3)
  pdf_storage_key?: string;
  pdf_mime_type?: string;
  pdf_size_bytes?: string | number;
  pdf_sha256?: string;
  gcs_permanent?: boolean;
  provisioning_status?: string;
  // Phase 7 §6.2 (#1531) — per-template canonical-key overrides. Empty array
  // for templates without overrides; the admin sensitive-keys UI uses this
  // to render the toggle state without a second round-trip.
  sensitive_key_overrides?: string[];
  created_at: string;
  updated_at: string;
  // Forward-compatibility: any new proto field arrives here without breaking
  // the type. Index signature only at the end so the typed fields above still
  // give autocomplete.
  [key: string]: unknown;
}

/** Wire-format `TemplateSeedRecord` from `crm-service.proto` (snake_case). */
export interface TemplateSeedRecordWire {
  id: string;
  kind: string;
  content_type?: string;
  name: string;
  description?: string;
  category?: string;
  subcategory?: string;
  tags?: string[];
  fields_json?: string;
  data_json?: string;
  external_id?: string;
  is_active?: boolean;
  must_fork_before_use?: boolean;
  legal_review_required?: boolean;
  created_at: string;
  updated_at: string;
  [key: string]: unknown;
}

// ============================================================================
// REQUEST / RESPONSE — listSubtypes / listSubcategories (Issue #1545)
// ============================================================================

/**
 * Type → Subtype picker (New Template modal step 2). For `kind=CONTRACT`
 * the gateway calls the CRM merged-view service. For `kind=TERMSHEET` the
 * gateway returns the hardcoded 2-row Structured / PDF Upload list (these
 * are code-path-bound, not a DB catalog). For DISCLOSURE / GENERIC the
 * picker has no step 2 — `listSubcategories` powers the free-text autocomplete
 * in the upload form.
 */
export const ListSubtypesRequestSchema = z.object({
  kind: TemplateKindSchema,
});
export type ListSubtypesRequest = z.infer<typeof ListSubtypesRequestSchema>;

/**
 * One subtype envelope, post-merge. For DB-backed kinds (CONTRACT) this is
 * the merged-view projection of `ContractSubtypeTemplate` + per-tenant
 * `ContractSubtype` overrides. For static kinds (TERMSHEET) it is the
 * hardcoded list. `id` is the per-tenant `ContractSubtype.id` for CONTRACT
 * (so create/update flows can target a specific row), and the slug for
 * static lists.
 */
export const SubtypeOptionSchema = z.object({
  id: z.string().min(1),
  slug: SubtypeSlugSchema,
  name: z.string().min(1),
  description: z.string().nullable().optional(),
  icon: z.string().nullable().optional(),
  sortOrder: z.number().int(),
  isActive: z.boolean(),
});
export type SubtypeOption = z.infer<typeof SubtypeOptionSchema>;

export const ListSubtypesResponseSchema = z.object({
  data: z.array(SubtypeOptionSchema),
});
export type ListSubtypesResponse = z.infer<typeof ListSubtypesResponseSchema>;

/**
 * Free-text autocomplete options for the Disclosure / Generic Subtype field.
 * Gateway runs `SELECT DISTINCT subcategory FROM templates WHERE business_id
 * = ? AND kind = ? AND subcategory IS NOT NULL` and returns the top values
 * by frequency.
 */
export const ListSubcategoriesRequestSchema = z.object({
  kind: TemplateKindSchema,
});
export type ListSubcategoriesRequest = z.infer<typeof ListSubcategoriesRequestSchema>;

export const ListSubcategoriesResponseSchema = z.object({
  data: z.array(z.string()),
});
export type ListSubcategoriesResponse = z.infer<typeof ListSubcategoriesResponseSchema>;
